import React from "react";
import { Spark, Delta } from "./TrendBits";
import { countryFlag, countryLabel } from "./CountryBreakdown";
import type { RoamingZone } from "./RoamingPulse";

// One panel for one zone on the overall map: how many are present and how
// that's moving, then the international slice and where it comes from. Either
// half is omitted when its data isn't loaded. Presentation only.

const label = "text-[11px] font-mono uppercase tracking-widest text-ink-faint";

export const SourceCountries: React.FC<{ countries: { country: string; count: number }[] }> = ({ countries }) => {
  const top = countries.slice(0, 8);
  const maxC = top[0]?.count || 1;
  if (!top.length) return <p className="text-xs text-ink-faint">No country breakdown for this zone.</p>;
  return (
    <ul className="mt-1.5 space-y-1.5">
      {top.map((c) => (
        <li key={c.country} className="text-xs">
          <div className="flex items-center gap-2">
            <span className="w-5 text-center" aria-hidden>
              {countryFlag(c.country)}
            </span>
            <span className="flex-1 truncate text-ink">{countryLabel(c.country)}</span>
            <span className="font-medium tabular-nums text-ink">{c.count.toLocaleString()}</span>
          </div>
          <div className="ml-7 mt-0.5 h-1 overflow-hidden rounded bg-paper-100">
            <div className="h-1 rounded bg-brand-500/80" style={{ width: `${(c.count / maxC) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

export interface AreaPresence {
  now: number | null;
  peak: number | null;
  delta: number | null; // vs 1h ago
  series: number[]; // shown range, oldest → newest
}

export const AreaDetailPanel: React.FC<{
  name: string;
  presence?: AreaPresence | null;
  zone?: RoamingZone | null;
  intervalLabel: string; // e.g. "10 min"
  onClose: () => void;
}> = ({ name, presence, zone, intervalLabel, onClose }) => (
  <div className="rounded-xl border border-paper-300/70 bg-surface/95 p-4 shadow-soft backdrop-blur">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className={label}>Zone</p>
        <p className="truncate font-display text-base font-semibold text-ink" title={name}>
          {name}
        </p>
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-ink-muted hover:bg-paper-100"
      >
        ×
      </button>
    </div>

    {presence && presence.now != null ? (
      <>
        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <p className={label}>Present now</p>
            <p className="font-display text-3xl font-bold tabular-nums text-ink">{presence.now.toLocaleString()}</p>
            {presence.peak != null && <p className="text-[11px] text-ink-faint">peak {presence.peak.toLocaleString()}</p>}
          </div>
          <div className="text-right">
            <p className={label}>Last 1h</p>
            {presence.delta != null && <Delta v={presence.delta} />}
          </div>
        </div>
        {presence.series.length > 1 && (
          <div className="mt-2">
            <Spark values={presence.series} delta={presence.delta ?? 0} width={272} height={32} />
          </div>
        )}
      </>
    ) : (
      <p className="mt-3 text-sm text-ink-muted">No presence data in the shown range.</p>
    )}

    {zone && (
      <div className="mt-3 border-t border-paper-200 pt-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className={label}>International now</p>
            <p className="font-display text-2xl font-bold tabular-nums text-ink">{zone.intlNow.toLocaleString()}</p>
            <p className="text-[11px] text-ink-faint">{zone.sharePct}% of all present</p>
          </div>
          <div className="text-right">
            <p className={label}>Last {intervalLabel}</p>
            <Delta v={zone.delta} />
          </div>
        </div>
        <div className="mt-2">
          <Spark values={zone.series} delta={zone.delta} width={272} height={32} />
        </div>
        <p className={`mt-3 ${label}`}>Source countries</p>
        <SourceCountries countries={zone.topCountries} />
      </div>
    )}
  </div>
);
